import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import store from '../../utils/store';
import {
  ACTION_TYPES_UPGRADE,
  VERSION_TYPES_UPGRADE,
  EDIT_MODES,
  LOADING_STATES
} from '../../constants/config_upgrade';

// ✅ NEW: Import utilities for text snippet updates
import {
  updateTextSnippetOnly,
  uploadTextSnippet,
  uploadVoiceSnippet,
  updateVoiceSnippetDb,
  createSpeechSampleRecord
} from '../slices/vaUtilitiesSlice';

// ✅ NEW: Import useMediaUpload hook for dependency injection
import { useMediaUpload } from '../../hooks/useMediaUpload';

// ✅ FIX: Use VoiceActor zone-based selectors
import {
  selectSharedDataForComponents,
  selectCharacterZoneData
} from '../selectors/zoneSelectors';

// ✅ FIX: Import ALL real actions from the VoiceActor slice
import {
  loadVariationForEdit,
  loadVersionForEdit,
  loadDefaultVersion,
  loadVersionForClone,
  saveVariation,
  updateVariation,
  updateActiveSample,
  updateVersionField,
  setActiveVersionFromSamples,
  deleteVersion,
  updateTextSnippetDb,
  updateFormField,
  setFormDirty,
  setFormSubmitting,
  setEditError,
  clearEditError,
  clearEditForm,
  clearUnsavedChanges,
  setRepositoryVersion,
  showErrorHint,
  fetchVariations // ✅ ADD: For refreshing variations list after save
} from '../slices/voiceActorDataSlice';

// ===== BASE SELECTORS =====
const selectVariationZone = (state) => state.user.voiceActorWorksheetData?.variationZone;

// ===== MEMOIZED SELECTORS FOR PERFORMANCE =====
const selectEditVersion = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVersion || null;
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[VA_SELECTOR] selectEditVersion:', result);
    }
    return result;
  }
);

const selectEditVariationData = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVariation || null;
    // Remove excessive logging - only log when explicitly debugging
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[VA_SELECTOR] selectEditVariationData :', result);
    }
    return result;
  }
);

const selectActiveVersion = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVersion || null;
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[VA_SELECTOR] selectActiveVersion:', result);
    }
    return result;
  }
);

const selectEditVersionSamples = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.samples || [];

    // Sort by version number (ascending: 1, 2, 3...)
    const sortedResult = [...result].sort((a, b) => {
      const versionA = parseInt(a.version) || 0;
      const versionB = parseInt(b.version) || 0;
      return versionA - versionB;
    });

    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[VA_SELECTOR] selectEditVersionSamples:', sortedResult?.length, 'samples (sorted by version)');
    }
    return sortedResult;
  }
);

const selectEditMode = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.editMode || 'view'
);

const selectEditLoadingState = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.loading ? 'LOADING' : 'IDLE'
);

const selectEditError = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.error || null
);

const selectHasUnsavedChanges = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.hasUnsavedChanges || false
);

const selectIsSubmitting = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.isSubmitting || false
);

const selectIsLoading = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.loading || false
);

// ✅ FIX: Create dummy selectors for fields that don't exist yet
const createDummySelector = (defaultValue) => () => defaultValue;

const selectCurrentAction = createDummySelector(null);
const selectEditErrorType = createDummySelector(null);
const selectIsFormDirty = createDummySelector(false);
const selectShowErrorHint = createDummySelector(false);
const selectErrorHintMessage = createDummySelector('');
const selectRepositoryVersion = createDummySelector(null);
const selectLastAction = createDummySelector(null);
const selectEditLastUpdated = createDummySelector(null);
const selectCanSave = createDummySelector(false);
const selectCanCancel = createDummySelector(false);
const selectFormValues = createDummySelector({});
// const selectSelections = createDummySelector({});

/**
 * Enhanced hook to manage edit version operations through Redux for VoiceActor
 * Provides a clean interface for all variation/version editing operations
 * Adapted from BookWorksheet useEditVersionManager for VoiceActor entity hierarchy
 */
const useEditVersionManager = () => {
  const dispatch = useDispatch();

  // ✅ NEW: Get uploadMedia function from useMediaUpload hook
  const { uploadMedia } = useMediaUpload();

  // Get current selections from VoiceActor zone-based selectors
  const sharedData = useSelector(selectSharedDataForComponents);
  const characterData = useSelector(selectCharacterZoneData);

  // Extract selections from VoiceActor data structure
  const actorId = sharedData?.selections?.actorId;
  const portfolioId = sharedData?.selections?.portfolioId;
  const characterId = sharedData?.selections?.characterId;

  // Create dummy objects for compatibility (commented out unused variables)
  // const selectedActor = actorId ? { uuid: actorId } : null;
  // const selectedPortfolio = portfolioId ? { uuid: portfolioId } : null;
  // const selectedCharacter = characterData?.selectedCharacter || (characterId ? { uuid: characterId } : null);

  // Get edit state
  const editVersion = useSelector(selectEditVersion);
  const editVariationData = useSelector(selectEditVariationData);
  const activeVersion = useSelector(selectActiveVersion);
  const samples = useSelector(selectEditVersionSamples);
  const editMode = useSelector(selectEditMode);
  const currentAction = useSelector(selectCurrentAction);
  const loadingState = useSelector(selectEditLoadingState);
  const error = useSelector(selectEditError);
  const errorType = useSelector(selectEditErrorType);
  const isDirty = useSelector(selectIsFormDirty);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const isSubmitting = useSelector(selectIsSubmitting);
  const showErrorHintState = useSelector(selectShowErrorHint);
  const errorHintMessage = useSelector(selectErrorHintMessage);
  const repositoryVersion = useSelector(selectRepositoryVersion);
  const lastAction = useSelector(selectLastAction);
  const lastUpdated = useSelector(selectEditLastUpdated);
  const canSave = useSelector(selectCanSave);
  const canCancel = useSelector(selectCanCancel);
  const isLoading = useSelector(selectIsLoading);
  const formValues = useSelector(selectFormValues);

  // ===== UTILITY HELPERS =====

  /**
   * Format validation errors for user display
   * @param {Array} errors - Array of error messages
   * @returns {string} Formatted error message
   */
  const formatValidationErrors = useCallback((errors) => {
    if (errors.length === 0) return '';
    if (errors.length === 1) return errors[0];

    return `Please fix the following issues before saving:\n\n${errors.map(error => `• ${error}`).join('\n')}`;
  }, []);

  /**
   * Comprehensive validation for variation submission with error handling (VoiceActor version)
   * @param {Object} params - Validation parameters
   * @param {Object} params.variationZone - Current variation zone state
   * @param {Object} params.selections - Current selections (actor, portfolio, character)
   * @param {string} params.characterId - Character ID
   * @param {Object} params.localFormState - Local form state
   * @param {string} params.editMode - Current edit mode
   * @param {Function} params.showHint - Function to show user hints
   * @param {boolean} params.show_logs - Flag to control console logging (default: true)
   * @returns {Object} { hasErrors: boolean, errorMessage: string, validationErrors: Array }
   */
  const validateVariationSubmission = useCallback((params) => {
    const {
      variationZone,
      selections,
      characterId,
      localFormState,
      editMode,
      showHint,
      show_logs = true
    } = params;

    const validationErrors = [];

    // 1. System-level validations
    if (!variationZone) {
      if (show_logs) console.error('[VA_validateVariationSubmission] No variation zone found in state');
      validationErrors.push('System error: No variation data found');

      // Early return for critical system error with immediate user feedback
      const errorMessage = 'System error: No variation data found';
      showHint(errorMessage);
      return {
        hasErrors: true,
        errorMessage,
        validationErrors,
        debugInfo: { criticalSystemError: true }
      };
    }

    // 2. Core data validations
    if (!variationZone.activeVariation?.name?.trim()) {
      if (show_logs) console.log('[VA_validateVariationSubmission] Missing variation name');
      validationErrors.push('Variation name is required');
    }

    if (!variationZone.activeVersion?.textSnippet?.trim()) {
      if (show_logs) console.log('[VA_validateVariationSubmission] Missing text content');
      validationErrors.push('Text content is required');
    }

    if (!characterId) {
      if (show_logs) console.log('[VA_validateVariationSubmission] Missing character ID');
      validationErrors.push('Character ID is required');
    }

    // 3. VoiceActor-specific validations (only when needed)
    if (localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT) {
      if (!selections?.actorId) {
        validationErrors.push('Voice Actor selection is required for file updates');
      }
      if (!selections?.portfolioId) {
        validationErrors.push('Portfolio selection is required for file updates');
      }
      if (!selections?.characterId) {
        validationErrors.push('Character selection is required for file updates');
      }

      // Additional S3-specific checks
      if (!variationZone.activeVariation?.uuid) {
        validationErrors.push('Variation ID is missing for file update');
      }

      if (!variationZone.activeVersion?.uuid) {
        validationErrors.push('Version ID is missing for file update');
      }
    }

    // 4. Content quality validations
    if (variationZone?.activeVersion?.textSnippet) {
      const contentLength = variationZone.activeVersion.textSnippet.length;

      if (contentLength > 10000) {
        validationErrors.push(`Text content is too long (${contentLength} characters). Maximum allowed is 10,000 characters.`);
      }

      if (contentLength < 10) {
        validationErrors.push('Text content is too short. Please provide at least 10 characters.');
      }
    }

    // 5. File naming validations
    if (variationZone?.activeVariation?.name &&
      !/^[a-zA-Z0-9\s\-_]+$/.test(variationZone.activeVariation.name)) {
      validationErrors.push('Variation name contains invalid characters for file storage');
    }

    // 6. Handle validation results with logging and user feedback
    if (validationErrors.length > 0) {
      if (show_logs) {
        console.group('[VA_validateVariationSubmission] Validation Failed');
        console.log('Total errors found:', validationErrors.length);
        console.log('Validation errors:', validationErrors);
        console.log('State snapshot:', {
          hasVariationZone: !!variationZone,
          hasActiveVariation: !!variationZone?.activeVariation,
          hasActiveVersion: !!variationZone?.activeVersion,
          variationName: variationZone?.activeVariation?.name,
          hasTextContent: !!variationZone?.activeVersion?.textSnippet,
          characterId,
          editMode,
          hasLocalChanges: localFormState?.hasLocalChanges,
          needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
        });
        console.groupEnd();
      }

      const errorMessage = formatValidationErrors(validationErrors);
      showHint(errorMessage);

      return {
        hasErrors: true,
        errorMessage,
        validationErrors,
        debugInfo: {
          totalErrors: validationErrors.length,
          needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
        }
      };
    }

    // 7. Success case
    if (show_logs) {
      console.log('[VA_validateVariationSubmission] All validations passed');
    }

    return {
      hasErrors: false,
      errorMessage: '',
      validationErrors: [],
      debugInfo: {
        needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
      }
    };
  }, [formatValidationErrors]);

  /**
   * Clear the entire form
   */
  const clearForm = useCallback(() => {
    dispatch(clearEditForm());
  }, [dispatch]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch(clearEditError());
  }, [dispatch]);

  /**
   * Show error hint for 3 seconds
   */
  const showHint = useCallback((message) => {
    dispatch(showErrorHint(message));

    // Auto-clear after 3 seconds
    setTimeout(() => {
      dispatch(clearEditError());
    }, 3000);
  }, [dispatch]);

  // ===== LOAD ACTIONS =====

  /**
   * Load a variation for editing (from VariationList selection)
   */
  const loadVariation = useCallback(async (variationId) => {
    if (!characterId || !actorId || !portfolioId) {
      console.error('[VA_useEditVersionManager] Missing required IDs for loadVariation');
      return;
    }

    console.log(`[VA_useEditVersionManager] Loading variation ${variationId} for edit`);

    try {
      dispatch(loadVariationForEdit({
        variationId,
        characterId,
        actorId,
        portfolioId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to load variation:', error);
    }
  }, [dispatch, characterId, actorId, portfolioId]);

  /**
   * Load a specific version for editing (from VersionsList row/edit/checkbox)
   */
  const loadVersion = useCallback(async (variationId, versionId) => {
    if (!characterId || !actorId || !portfolioId) {
      console.error('[VA_useEditVersionManager] Missing required IDs for loadVersion');
      return;
    }

    console.log(`[VA_useEditVersionManager] Loading version ${versionId} for variation ${variationId}`);

    try {
      dispatch(loadVersionForEdit({
        variationId,
        versionId,
        characterId,
        actorId,
        portfolioId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to load version:', error);
    }
  }, [dispatch, characterId, actorId, portfolioId]);

  /**
   * Load default version for new variation (from New Variation button)
   */
  const loadDefault = useCallback(async () => {
    if (!characterId) {
      console.error('[VA_useEditVersionManager] Missing characterId for loadDefault');
      return;
    }

    console.log(`[VA_useEditVersionManager] Loading default version for character ${characterId}`);

    try {
      dispatch(loadDefaultVersion({ characterId }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to load default version:', error);
    }
  }, [dispatch, characterId]);

  /**
   * Load version for cloning (from Use as Base button)
   */
  const loadClone = useCallback(async (variationId, versionId) => {
    if (!characterId || !actorId || !portfolioId) {
      console.error('[VA_useEditVersionManager] Missing required IDs for loadClone');
      return;
    }

    console.log(`[VA_useEditVersionManager] Loading version ${versionId} for cloning`);

    try {
      dispatch(loadVersionForClone({
        variationId,
        versionId,
        characterId,
        actorId,
        portfolioId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to load version for cloning:', error);
    }
  }, [dispatch, characterId, actorId, portfolioId]);

  const setActiveVersionFromSamplesHook = useCallback((versionId) => {
    console.log('[VA_useEditVersionManager] Setting active version from samples:', versionId);
    dispatch(setActiveVersionFromSamples(versionId));
  }, [dispatch]);

  // ===== SAVE ACTIONS =====

  /**
   * Save the current variation (handles new, update, and clone modes)
   */
  const save = useCallback(async () => {
    if (!editVariationData || !activeVersion || !characterId) {
      console.error('[VA_useEditVersionManager] Missing required data for save');
      return;
    }

    console.log(`[VA_useEditVersionManager] Saving variation in ${editMode} mode. activeVersion ${activeVersion}`);
    // const tmp_activeVersion = store.getState().user.voiceActorData.variationZone.activeVersion;

    try {
      dispatch(saveVariation({
        variationData: editVariationData,
        versionData: activeVersion,
        editMode,
        characterId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to save variation:', error);
    }
  }, [dispatch, editVariationData, activeVersion, editMode, characterId]);

  // ===== DELETE ACTIONS =====

  /**
   * Delete a version (from VersionsList delete button)
   */
  const deleteVersionById = useCallback(async (versionId, variationId) => {
    if (!characterId) {
      console.error('[VA_useEditVersionManager] Missing characterId for deleteVersion');
      return;
    }

    console.log(`[VA_useEditVersionManager] Deleting version ${versionId}`);

    try {
      dispatch(deleteVersion({
        versionId,
        variationId,
        characterId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to delete version:', error);
    }
  }, [dispatch, characterId]);

  /**
   * Delete entire variation (from form Delete Variation button)
   */
  const deleteVariation = useCallback(async (variationId, options = {}) => {
    if (!variationId) {
      throw new Error('Variation ID is required');
    }

    if (!characterId) {
      throw new Error('Character ID is required');
    }

    console.log(`[VA_useEditVersionManager] Deleting variation ${variationId} with enhanced delete system`);

    try {
      const result = dispatch(deleteVariation({
        variationId
      }));

      console.log('✅ Variation deleted successfully with enhanced delete system:', result);
      return result;
    } catch (error) {
      console.error('❌ Enhanced delete failed:', error);
      throw error;
    }
  }, [dispatch, characterId]);

  // ===== ACTIVE SAMPLE ACTIONS =====

  /**
   * Update the active sample for a variation (from VersionsList checkbox)
   */
  const setActiveSample = useCallback(async (variationId, sampleId) => {
    if (!characterId) {
      console.error('[VA_useEditVersionManager] Missing characterId for setActiveSample');
      return;
    }

    console.log(`[VA_useEditVersionManager] Setting active sample ${sampleId} for variation ${variationId}`);

    try {
      dispatch(updateActiveSample({
        variationId,
        sampleId,
        characterId
      }));
    } catch (error) {
      console.error('[VA_useEditVersionManager] Failed to update active sample:', error);
    }
  }, [dispatch, characterId]);

  // ===== FORM FIELD ACTIONS =====

  /**
   * Update a variation field (name, priority, status, etc.)
   */
  const updateVariationField = useCallback((field, value) => {
    console.log(`[VA_useEditVersionManager] Updating variation field ${field}:`, value);
    dispatch(updateFormField({ field, value }));
  }, [dispatch]);

  /**
   * Update a version field (textSnippet, s3Url, etc.)
   */
  const updateActiveVersionField = useCallback(async (field, value) => {
    // Conditional logging for performance
    if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_FORM) {
      console.log(`[VA_useEditVersionManager] Updating version field ${field}:`, value);
    }
    dispatch(updateVersionField({ field, value }));
  }, [dispatch]);

  /**
   * ✅ NEW: Update text snippet only for existing version (VoiceActor version)
   * This allows updating text content independently of audio
   */
  const updateTextSnippetForVersion = useCallback(async (newTextContent) => {
    if (!activeVersion?.uuid || !editVariationData?.uuid) {
      console.error('[VA_useEditVersionManager] Missing version or variation ID for text update');
      showHint('Cannot update text: missing version information');
      return { success: false, error: 'Missing version information' };
    }

    try {
      console.log('[VA_useEditVersionManager] Updating text snippet for version:', activeVersion.uuid);

      // Get fresh metadata from state
      const currentState = store.getState();
      const selections = currentState.user.voiceActorWorksheetData?.sharedData?.selections;

      const metadata = {
        actorId: selections?.actorId || actorId,
        portfolioId: selections?.portfolioId || portfolioId,
        characterId: selections?.characterId || characterId,
        variationName: editVariationData.name || 'Unnamed Variation'
      };

      // Use the utilities slice to update text snippet only
      const result = await dispatch(updateTextSnippetOnly({
        versionId: activeVersion.uuid,
        newTextContent,
        variationId: editVariationData.uuid,
        metadata
      }));

      if (result.type.endsWith('/fulfilled')) {
        console.log('[VA_useEditVersionManager] Text snippet updated successfully');

        // Update local Redux state to reflect the change
        dispatch(updateVersionField({ field: 'textSnippet', value: newTextContent }));

        return { success: true, result: result.payload };
      } else {
        console.error('[VA_useEditVersionManager] Text snippet update failed:', result.payload);
        showHint('Failed to update text snippet');
        return { success: false, error: result.payload };
      }
    } catch (error) {
      console.error('[VA_useEditVersionManager] Error updating text snippet:', error);
      showHint('Failed to update text snippet');
      return { success: false, error: error.message };
    }
  }, [dispatch, activeVersion, editVariationData, actorId, portfolioId, characterId, showHint]);

  // ===== UI STATE ACTIONS =====

  /**
   * Set form dirty state
   */
  const setDirty = useCallback((dirty) => {
    dispatch(setFormDirty(dirty));
  }, [dispatch]);

  /**
   * Set form submitting state
   */
  const setSubmitting = useCallback((submitting) => {
    dispatch(setFormSubmitting(submitting));
  }, [dispatch]);

  /**
   * Set error state
   */
  const setError = useCallback((error, errorType) => {
    dispatch(setEditError({ error, errorType }));
  }, [dispatch]);

  /**
   * Set repository version for cancel operations
   */
  const setRepository = useCallback((version) => {
    dispatch(setRepositoryVersion(version));
  }, [dispatch]);

  // ===== CONVENIENCE METHODS =====

  /**
   * Cancel changes and revert to repository version
   */
  const cancel = useCallback(() => {
    if (repositoryVersion) {
      // Reload from repository
      if (repositoryVersion.variation?.uuid) {
        loadVariation(repositoryVersion.variation.uuid);
      } else {
        loadDefault();
      }
    } else {
      clearForm();
    }
  }, [repositoryVersion, loadVariation, loadDefault, clearForm]);

  /**
   * Check if the form can be saved
   */
  const validateForSave = useCallback(() => {
    if (!editVariationData?.name?.trim()) {
      showHint('Variation name is required');
      return false;
    }

    if (!activeVersion?.textSnippet?.trim()) {
      showHint('Text content is required');
      return false;
    }

    return true;
  }, [editVariationData, activeVersion, showHint]);

  /**
   * Save with validation
   */
  const saveWithValidation = useCallback(async () => {
    if (!validateForSave()) {
      return false;
    }
    console.log('ACTIVE VERSION', store.getState().user.voiceActorWorksheetData.variationZone.activeVersion.textSnippet);
    try {
      await save();
      return true;
    } catch (error) {
      showHint('Failed to save variation');
      return false;
    }
  }, [validateForSave, save, showHint]);

  /**
   * ✅ ENHANCED: Dual workflow variation submit function
   * Supports both "New Variation (Text-Only)" and "New Variation (With Recording)" workflows
   * Ensures variation is created BEFORE voice upload to get real variationId
   */
  const variationSubmit = useCallback(async (localFormState, pendingRecording = null) => {
    try {
      const show_logs = process.env.NODE_ENV === 'development'; // Control logging based on environment

      if (show_logs) console.log('[VA_variationSubmit] Starting submit process with localFormState:', localFormState);

      // 1. Force sync any pending local changes to Redux
      if (localFormState?.hasLocalChanges && localFormState?.content !== undefined) {
        if (show_logs) console.log('[VA_variationSubmit] Syncing local changes to Redux');
        dispatch(updateVersionField({
          field: 'textSnippet',
          value: localFormState.content
        }));
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // 2. Get fresh state for validation and operations
      const currentState = store.getState();
      const variationZone = currentState.user.voiceActorWorksheetData?.variationZone;
      const selections = currentState.user.voiceActorWorksheetData?.sharedData?.selections;

      // 3. Comprehensive validation with quick return
      const validationResult = validateVariationSubmission({
        variationZone,
        selections,
        characterId,
        localFormState,
        editMode,
        showHint,
        show_logs
      });

      // 4. Quick return if validation failed
      if (validationResult.hasErrors) {
        return {
          success: false,
          error: 'Validation failed',
          validationErrors: validationResult.validationErrors,
          debugInfo: validationResult.debugInfo
        };
      }

      // 5. ✅ ENHANCED: Create/Update variation FIRST (BOTH workflows)
      let realVariationId = variationZone.activeVariation?.uuid;
      let realVersionId = variationZone.activeVersion?.uuid;

      if (show_logs) console.log('[VA_variationSubmit] Starting main save operation');
      const saveResult = await dispatch(saveVariation({
        variationData: variationZone.activeVariation,
        versionData: variationZone.activeVersion,
        editMode,
        characterId
      })).unwrap();

      // ✅ ENHANCED: Extract real IDs from save result
      if (editMode === 'create' || editMode === 'clone') {
        // For new variations, get the real ID from the save result
        realVariationId = saveResult.variation?.uuid;
        if (show_logs) console.log('[VA_variationSubmit] New variation created with ID:', realVariationId);
      }

      // 6. S3 text update (only if validation passed and needed)
      if (validationResult.debugInfo.needsS3Update && realVariationId && realVersionId) {
        if (show_logs) console.log('[VA_variationSubmit] Text content changed in edit mode, updating S3 file');

        try {
          const s3Metadata = {
            actorId: selections.actorId,
            portfolioId: selections.portfolioId,
            characterId: selections.characterId,
            variationName: variationZone.activeVariation.name
          };

          const textUpdateResult = await dispatch(uploadTextSnippet({
            textContent: variationZone.activeVersion.textSnippet,
            variationId: realVariationId,
            speechSampleId: realVersionId,
            metadata: s3Metadata,
            uploadMedia // ✅ Pass the uploadMedia function from the hook
          })).unwrap();

          await dispatch(updateTextSnippetDb({
            portfoliouuid: selections.portfolioId,
            sampleuuid: realVersionId,
            content: variationZone.activeVersion.textSnippet,
          }));

          if (show_logs) console.log('[VA_variationSubmit] S3 text file updated successfully:', textUpdateResult.textS3Url);
        } catch (error) {
          console.error('[VA_variationSubmit] Failed to update S3 text file:', error);
          showHint('Failed to update text file in storage. Please try again.');
          return { success: false, error: 'S3 text update failed' };
        }
      }

      // 7. ✅ ENHANCED: Upload text snippet for ALL new variations (create/clone modes)
      let textUploadResult = null;
      if ((editMode === 'create' || editMode === 'clone') && realVariationId) {
        if (show_logs) console.log('[VA_variationSubmit] Uploading text snippet for new variation');

        try {
          const textMetadata = {
            actorId: selections.actorId,
            portfolioId: selections.portfolioId,
            characterId: selections.characterId,
            variationName: variationZone.activeVariation.name
          };

          textUploadResult = await dispatch(uploadTextSnippet({
            textContent: variationZone.activeVersion.textSnippet,
            variationId: realVariationId,
            speechSampleId: realVariationId, // Use variation UUID as sample ID
            metadata: textMetadata,
            uploadMedia // ✅ Pass the uploadMedia function from the hook
          })).unwrap();

          if (show_logs) console.log('[VA_variationSubmit] Text snippet uploaded successfully:', textUploadResult.textS3Url);
        } catch (error) {
          console.error('[VA_variationSubmit] Failed to upload text snippet:', error);
          showHint('Failed to upload text snippet. Please try again.');
          return { success: false, error: 'Text upload failed' };
        }
      }

      // 8. ✅ ENHANCED: Conditional voice snippet upload (DUAL WORKFLOW SUPPORT)
      let voiceUpdateResult = null;
      if (pendingRecording?.audioBlob && realVariationId) {
        // WORKFLOW B: New Variation WITH Recording
        if (show_logs) console.log('[VA_variationSubmit] Processing new variation with recording - variationId:', realVariationId);

        try {
          const voiceMetadata = {
            actorId: selections.actorId,
            portfolioId: selections.portfolioId,
            characterId: selections.characterId,
            variationName: variationZone.activeVariation.name,
            fileDuration: pendingRecording.metadata?.fileDuration || 0
          };

          // ✅ ENHANCED: Use real variationId for voice upload
          voiceUpdateResult = await dispatch(uploadVoiceSnippet({
            audioBlob: pendingRecording.audioBlob,
            variationId: realVariationId, // ✅ Use real ID
            speechSampleId: realVariationId, // ✅ Use variation UUID as sample ID
            metadata: voiceMetadata,
            uploadMedia // ✅ Pass the uploadMedia function from the hook
          })).unwrap();

          // Update the variation with the new voice S3 URL and duration
          await dispatch(updateVariation({
            variationId: realVariationId,
            actorId: selections.actorId,      // ✅ FIXED: Add required hierarchy parameters
            portfolioId: selections.portfolioId, // ✅ FIXED: Add required hierarchy parameters  
            characterId: selections.characterId, // ✅ FIXED: Add required hierarchy parameters
            data: {
              s3Url: voiceUpdateResult.voiceS3Url,
              fileDuration: pendingRecording.metadata?.fileDuration || 0
            }
          }));

          // ✅ FIX: Update activeVersion to sync main form audio player
          updateActiveVersionField('s3Url', voiceUpdateResult.voiceS3Url);
          updateActiveVersionField('fileDuration', pendingRecording.metadata?.fileDuration || 0);

          if (show_logs) console.log('[VA_variationSubmit] New variation created with recording successfully:', voiceUpdateResult.voiceS3Url);
        } catch (error) {
          console.error('[VA_variationSubmit] Failed to upload voice recording:', error);
          showHint('Failed to upload voice recording. Please try again.');
          return { success: false, error: 'Voice upload failed' };
        }
      } else if (pendingRecording?.audioBlob && !realVariationId) {
        console.error('[VA_variationSubmit] Cannot upload voice: no real variationId available');
        showHint('Failed to upload voice recording: variation not created properly.');
        return { success: false, error: 'Voice upload failed - no variation ID' };
      } else {
        // WORKFLOW A: New Variation WITHOUT Recording (Text-Only)
        if (show_logs) console.log('[VA_variationSubmit] New variation created (text-only) - no recording to upload');
      }

      // 8.1. ✅ CRITICAL: Create CharSpeechSample database nodes (MISSING STEP!)
      if ((editMode === 'create' || editMode === 'clone') && realVariationId && (textUploadResult || voiceUpdateResult)) {
        if (show_logs) console.log('[VA_variationSubmit] Creating CharSpeechSample database nodes');

        try {
          const speechSampleData = {
            speechSampleId: realVariationId, // Use variation UUID as sample ID
            variationId: realVariationId,
            variationName: variationZone.activeVariation.name,
            version: variationZone.activeVariation.version || 1,
            voiceS3Url: voiceUpdateResult?.voiceS3Url || null,
            textS3Url: textUploadResult?.textS3Url || null,
            textContent: variationZone.activeVersion.textSnippet,
            fileDuration: voiceUpdateResult?.fileDuration || pendingRecording?.metadata?.fileDuration || 0,
            metadata: {
              actorId: selections.actorId,
              portfolioId: selections.portfolioId,
              characterId: selections.characterId,
              characterName: characterData?.selectedCharacter?.name || 'Unknown Character'
            }
          };

          const recordResult = await dispatch(createSpeechSampleRecord({
            speechSampleData
          }));

          if (recordResult.type.endsWith('/fulfilled')) {
            if (show_logs) console.log('[VA_variationSubmit] ✅ CharSpeechSample nodes created successfully in database');
          } else {
            console.error('[VA_variationSubmit] ❌ Failed to create CharSpeechSample nodes:', recordResult.payload);
            showHint('Warning: Files uploaded but database records may be incomplete. Please contact support if issues persist.');
            // Don't fail the entire operation - files are uploaded successfully
          }
        } catch (error) {
          console.error('[VA_variationSubmit] ❌ Error creating CharSpeechSample nodes:', error);
          showHint('Warning: Files uploaded but database records may be incomplete. Please contact support if issues persist.');
          // Don't fail the entire operation - files are uploaded successfully
        }
      }

      // 8.5. ✅ ZONE REFRESH: Reload variation to update audio player and recording controls
      if ((editMode === 'create' || editMode === 'clone') && realVariationId && pendingRecording?.audioBlob) {
        if (show_logs) console.log('[VA_variationSubmit] Refreshing variation zone after voice upload to show audio player');

        try {
          await dispatch(loadVariationForEdit({
            variationId: realVariationId,
            characterId,
            actorId: selections.actorId,
            portfolioId: selections.portfolioId
          }));
          if (show_logs) console.log('[VA_variationSubmit] Variation zone refreshed - audio player should now appear');
        } catch (error) {
          console.error('[VA_variationSubmit] Failed to refresh variation zone:', error);
          // Don't fail the entire operation for this - it's a UI enhancement
        }
      }

      // 9. ✅ ENHANCED: Refresh variations list after successful save (for new variations)
      if ((editMode === 'create' || editMode === 'clone') && characterId && portfolioId) {
        if (show_logs) console.log('[VA_variationSubmit] Refreshing variations list after successful save');

        try {
          await dispatch(fetchVariations({ characterId, portfolioId }));
          if (show_logs) console.log('[VA_variationSubmit] Variations list refreshed successfully');
        } catch (error) {
          console.error('[VA_variationSubmit] Failed to refresh variations list:', error);
          // Don't fail the entire operation for this - it's a UI enhancement
        }
      }

      // 10. ✅ CLEAR UNSAVED CHANGES: Reset button state after successful operation
      if (show_logs) console.log('[VA_variationSubmit] Clearing unsaved changes state');
      dispatch(clearUnsavedChanges());

      // 11. ✅ CLEAR ACTIVE VARIATION: Reset variations list selection ONLY for new variations
      if ((editMode === 'create' || editMode === 'clone')) {
        if (show_logs) console.log('[VA_variationSubmit] Clearing active variation selection for new variation');
        dispatch(clearEditForm());
      } else if (editMode === 'edit') {
        // For edit mode, keep the current variation selected in the variations list
        if (show_logs) console.log('[VA_variationSubmit] Keeping current variation selected after update');
        // The variation should remain selected - no action needed
      }

      if (show_logs) console.log('[VA_variationSubmit] All operations completed successfully');
      return { success: true };

    } catch (error) {
      console.error('[VA_variationSubmit] Failed:', error);
      showHint('Failed to save variation');
      return { success: false, error: error.message };
    }
  }, [dispatch, editMode, characterId, showHint, validateVariationSubmission, uploadMedia]);

  // ===== RETURN INTERFACE =====

  return {
    // State
    editVersion,
    editVariationData,
    activeVersion,
    samples,
    editMode,
    currentAction,
    loadingState,
    error,
    errorType,
    isDirty,
    hasUnsavedChanges,
    isSubmitting,
    showErrorHint: showErrorHintState,  // Boolean state
    errorHintMessage,
    repositoryVersion,
    lastAction,
    lastUpdated,
    canSave,
    canCancel,
    isLoading,
    formValues,

    // Load actions
    loadVariation,
    loadVersion,
    loadDefault,
    loadClone,

    // Save actions
    save,
    saveWithValidation,
    variationSubmit,

    // Delete actions
    deleteVersionById,
    deleteVariation,

    // Active sample actions
    setActiveSample,
    setActiveVersionFromSamples: setActiveVersionFromSamplesHook,

    // Form field actions
    updateVariationField,
    updateActiveVersionField,
    updateTextSnippetForVersion,

    // UI state actions
    setDirty,
    setSubmitting,
    setError,
    clearError,
    showHint,
    clearForm,
    setRepository,

    // Convenience methods
    cancel,
    validateForSave,

    // Constants for components
    ACTION_TYPES: ACTION_TYPES_UPGRADE,
    VERSION_TYPES: VERSION_TYPES_UPGRADE,
    EDIT_MODES,
    LOADING_STATES
  };
};

export default useEditVersionManager;
